import React, { useState, useMemo, useEffect, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import * as subscriptionService from '../services/subscriptionService';
import type { MySubscription } from '../services/subscriptionService';
import PurchaseComingSoonModal from '../components/subscription/PurchaseComingSoonModal';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import './PricingPage.css';

const SwapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

const CheckSubtle = () => (
  <svg className="pricing-compare__check-subtle" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);

const CheckPremium = () => (
  <svg className="pricing-compare__check-premium" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#e9ecff" />
    <polyline points="7 12 11 16 17 9" fill="none" stroke="#667eea" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BulletCheck = () => (
  <svg className="pricing-summary__bullet" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);

const BulletMinus = () => (
  <svg className="pricing-summary__bullet is-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.39 6.72L21 11l-6.61 2.28L12 20l-2.39-6.72L3 11l6.61-2.28z" />
  </svg>
);

const AdPlay = () => (
  <svg className="pricing-compare__ad-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#f3e8ff" />
    <polygon points="10 8 16.5 12 10 16" fill="#8b5cf6" />
  </svg>
);

type CellState = 'yes' | 'ad' | 'no';

interface FeatureRow {
  key: string;
  subKey?: string;
  free: CellState;
  premiumKey?: string;
}

interface FeatureSection {
  titleKey: string;
  features: FeatureRow[];
}

const FEATURE_SECTIONS: FeatureSection[] = [
  {
    titleKey: 'compare.section.basic',
    features: [
      { key: 'compare.feature.browse', free: 'yes' },
      { key: 'compare.feature.reading', free: 'yes' },
      { key: 'compare.feature.dict', free: 'yes' },
      { key: 'compare.feature.cefr', free: 'yes' },
      { key: 'compare.feature.kg', free: 'yes' },
      { key: 'compare.feature.theme', free: 'yes' },
      { key: 'compare.feature.forum', free: 'yes' },
      { key: 'compare.feature.dm', free: 'yes' },
    ],
  },
  {
    titleKey: 'compare.section.advanced',
    features: [
      { key: 'compare.feature.review', free: 'ad' },
      { key: 'compare.feature.dictation', free: 'ad' },
      { key: 'compare.feature.shadow', free: 'ad' },
      { key: 'compare.feature.syncShadow', subKey: 'compare.feature.syncShadowSub', free: 'ad' },
      { key: 'compare.feature.quiz', free: 'ad' },
      { key: 'compare.feature.writing', free: 'ad' },
      { key: 'compare.feature.aiAnswer', free: 'ad' },
    ],
  },
  {
    titleKey: 'compare.section.personalized',
    features: [
      // Placement test is the new-learner hook — fully open to free users.
      { key: 'compare.feature.placement', subKey: 'compare.feature.placementSub', free: 'yes' },
      { key: 'compare.feature.report', free: 'no', premiumKey: 'compare.weekly' },
      { key: 'compare.feature.aiChat', free: 'ad' },
    ],
  },
  {
    titleKey: 'compare.section.creator',
    features: [
      { key: 'compare.feature.upload', free: 'no' },
      { key: 'compare.feature.aiMaterial', free: 'no' },
      { key: 'compare.feature.dialogueEdit', free: 'no' },
    ],
  },
];

// 2026-05-15 pricing — yearly = monthly × 10 (16.67% off, "2 months free",
// SaaS industry standard). Replaces the legacy ~35%-off numbers that lived in
// test mode prior to live billing launch. Must stay in sync with the
// `STRIPE_PRICE_*` env IDs created by stripe-create-live-prices.sh; see
// `project_stripe_live_prices.md` memory for the canonical table.
// `yearlyMonthly` is the displayed "effective monthly cost when billed yearly"
// — round to whole units to match local typography conventions.
const REGIONAL_PRICING: Record<string, { monthly: string; yearly: string; yearlyMonthly: string; currency: string; symbol: string; approximate: boolean }> = {
  'en':    { monthly: '13',      yearly: '130',       yearlyMonthly: '10.83', currency: 'USD', symbol: '$',   approximate: false },
  'zh-TW': { monthly: '250',     yearly: '2,500',     yearlyMonthly: '208',   currency: 'TWD', symbol: 'NT$', approximate: true  },
  'zh-CN': { monthly: '40',      yearly: '400',       yearlyMonthly: '33',    currency: 'CNY', symbol: '¥',   approximate: false },
  'ja':    { monthly: '1,280',   yearly: '12,800',    yearlyMonthly: '1,067', currency: 'JPY', symbol: '¥',   approximate: false },
  'ko':    { monthly: '12,900',  yearly: '129,000',   yearlyMonthly: '10,750', currency: 'KRW', symbol: '₩',  approximate: false },
  'th':    { monthly: '249',     yearly: '2,490',     yearlyMonthly: '208',   currency: 'THB', symbol: '฿',   approximate: true  },
  'vi':    { monthly: '149,000', yearly: '1,490,000', yearlyMonthly: '124,167', currency: 'VND', symbol: '₫', approximate: true  },
  'id':    { monthly: '99,000',  yearly: '990,000',   yearlyMonthly: '82,500', currency: 'IDR', symbol: 'Rp', approximate: false },
  'ms':    { monthly: '30',      yearly: '300',       yearlyMonthly: '25',    currency: 'MYR', symbol: 'RM',  approximate: false },
};

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation('pricing');
  const { user, isAuthenticated, refreshUser } = useAuth();

  // SEO for /pricing — uses pricing-namespace title/subtitle, augmented with
  // WebPage + BreadcrumbList JSON-LD. We deliberately do NOT emit a
  // schema.org/Offer here because the live price varies by region and the
  // pricing table itself spells out the offer; rolling a single Offer node
  // could mislead Google about the headline price.
  const pricingTitle = t('title');
  const pricingSubtitle = t('subtitle');
  const seoTags = useSeoTags({
    title: pricingTitle,
    description: pricingSubtitle,
    canonical: '/pricing',
    jsonLd: buildWebPageJsonLd({
      url: '/pricing',
      name: pricingTitle,
      description: pricingSubtitle,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: pricingTitle, url: '/pricing' },
      ],
    }),
  });

  const [isYearly, setIsYearly] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [mySub, setMySub] = useState<MySubscription | null>(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSource, setWaitlistSource] = useState<string>('pricing_cta');

  // 2026-05-15: live billing open. Any authenticated user can complete a real
  // Stripe checkout. Pre-launch gate (`accountType==='internal'`) removed per
  // the 2026-05-10 pricing × permission decisions, once live prices were
  // created (see project_stripe_live_prices.md) and the live webhook signing
  // secret was set in Fly. Unauthenticated visitors still see the price page
  // but the Subscribe CTA gates them to /login (handled below).
  const canPurchase = isAuthenticated;

  const isSubscribed = !!user && subscriptionService.isSubscriptionActive(user.subscriptionStatus);
  const isTrialing = user?.subscriptionStatus === 'trialing';

  const trialEndsAt = user?.trialEndsAt ? new Date(user.trialEndsAt) : null;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const needsUpgrade = !!user && !isSubscribed;
  const formatDate = (date: Date) => date.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  const pricing = useMemo(() => {
    const lang = i18n.language;
    return REGIONAL_PRICING[lang] || REGIONAL_PRICING['en'];
  }, [i18n.language]);

  const fetchSubscription = useCallback(async () => {
    if (!isSubscribed) return;
    try {
      const sub = await subscriptionService.getMySubscription();
      setMySub(sub);
    } catch {
      /* non-critical */
    }
  }, [isSubscribed]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const currentPeriod = mySub?.billingPeriod || null;
  const targetPeriod: 'monthly' | 'yearly' = currentPeriod === 'yearly' ? 'monthly' : 'yearly';
  const isUpgrade = targetPeriod === 'yearly';

  const extractErrorMessage = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

  const handleUpgrade = async (source = 'pricing_summary') => {
    if (!canPurchase) {
      setWaitlistSource(source);
      setWaitlistOpen(true);
      return;
    }
    try {
      setError(null);
      const period: 'monthly' | 'yearly' = isYearly ? 'yearly' : 'monthly';
      await subscriptionService.redirectToCheckout(i18n.language, period);
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Failed to start checkout'));
    }
  };

  const handleManageSubscription = async () => {
    try {
      setError(null);
      await subscriptionService.redirectToPortal();
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Failed to open customer portal'));
    }
  };

  const handleSwitchPlan = async () => {
    if (!canPurchase) {
      setWaitlistSource('pricing_switch');
      setWaitlistOpen(true);
      return;
    }
    try {
      setError(null);
      await subscriptionService.switchPlan(i18n.language, targetPeriod);
      setSuccessMsg(t('switchSuccess', 'Subscription updated!'));
      await fetchSubscription();
      refreshUser();
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Failed to switch plan'));
    }
  };

  const priceAmount = isYearly ? pricing.yearlyMonthly : pricing.monthly;

  const renderFreeCell = (state: CellState) => {
    if (state === 'yes') return <CheckSubtle />;
    if (state === 'ad') {
      return (
        <>
          <AdPlay />
          <span className="pricing-compare__ad-label">{t('compare.watchAd')}</span>
        </>
      );
    }
    return null;
  };

  return (
    <article className="pricing-page">
      {seoTags}
      <div className="pricing-wrap">
        <header className="pricing-hero">
          <h1 className="pricing-hero__title">{t('title')}</h1>
          <p className="pricing-hero__subtitle">{t('subtitle')}</p>
        </header>

        {isSubscribed && (
          <section className="pricing-status">
            <span className="pricing-status__badge">
              {isTrialing ? t('trialActive') : t('premiumActive')}
            </span>
            {isTrialing && trialEndsAt && trialDaysLeft !== null && (
              <span className={`pricing-status__meta ${trialDaysLeft <= 3 ? 'is-urgent' : ''}`}>
                {t('trialEndsOn', { date: formatDate(trialEndsAt) })}
                {trialDaysLeft > 0
                  ? ` · ${t('daysLeft', { count: trialDaysLeft })}`
                  : ` · ${t('trialEndsToday')}`}
              </span>
            )}
            {!isTrialing && currentPeriod && (
              <span className="pricing-status__meta">
                {currentPeriod === 'yearly' ? t('yearly') : t('monthly')}
              </span>
            )}
            <button
              className="pricing-status__manage"
              onClick={handleManageSubscription}
            >
              {isTrialing ? t('premium.cta') : t('manageSubscription')}
            </button>
          </section>
        )}

        {error && (
          <div className="pricing-toast is-error" role="alert">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        {successMsg && (
          <div className="pricing-toast is-success" role="status">
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        <div className="pricing-toggle" role="tablist" aria-label="Billing period">
          <button
            role="tab"
            aria-selected={!isYearly}
            className={`pricing-toggle__tab ${!isYearly ? 'is-active' : ''}`}
            onClick={() => setIsYearly(false)}
          >
            {t('monthly')}
          </button>
          <button
            role="tab"
            aria-selected={isYearly}
            className={`pricing-toggle__tab ${isYearly ? 'is-active' : ''}`}
            onClick={() => setIsYearly(true)}
          >
            {t('yearly')}
            <span className="pricing-toggle__save">{t('save', { percent: 36 })}</span>
          </button>
        </div>

        <section className="pricing-summary" aria-label="Plan summary">
          <article className="pricing-summary__card is-free">
            <header className="pricing-summary__header">
              <span className="pricing-summary__plan">{t('compare.free')}</span>
              <div className="pricing-summary__price">
                <span className="pricing-summary__sym">{pricing.symbol}</span>
                <span className="pricing-summary__amt">0</span>
                <span className="pricing-summary__per">/{t('perMonthShort')}</span>
              </div>
              <p className="pricing-summary__tagline">{t('summary.free.tagline')}</p>
            </header>
            <ul className="pricing-summary__list">
              {(t('summary.free.items', { returnObjects: true }) as string[]).map((item, i) => (
                <li key={i} className="pricing-summary__item">
                  <BulletCheck />
                  <span>{item}</span>
                </li>
              ))}
              {(t('summary.free.limitations', { returnObjects: true }) as string[]).map((item, i) => (
                <li key={`lim-${i}`} className="pricing-summary__item is-muted">
                  <BulletMinus />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="pricing-summary__card is-premium">
            <span className="pricing-summary__badge">
              <SparkleIcon />
              {t('summary.premium.badge')}
            </span>
            <header className="pricing-summary__header">
              <span className="pricing-summary__plan">{t('compare.premium')}</span>
              <div className="pricing-summary__price">
                {isYearly && pricing.approximate && <span className="pricing-summary__approx">≈</span>}
                <span className="pricing-summary__sym">{pricing.symbol}</span>
                <span className="pricing-summary__amt">{priceAmount}</span>
                <span className="pricing-summary__per">/{t('perMonthShort')}</span>
              </div>
              {isYearly ? (
                <p className="pricing-summary__tagline">
                  {t('billedYearly', { symbol: pricing.symbol, amount: pricing.yearly })}
                  <span className="pricing-summary__save"> · {t('save', { percent: 36 })}</span>
                </p>
              ) : (
                <p className="pricing-summary__tagline">{t('summary.premium.tagline')}</p>
              )}
            </header>
            <ul className="pricing-summary__list">
              {(t('summary.premium.items', { returnObjects: true }) as string[]).map((item, i) => (
                <li key={i} className="pricing-summary__item is-primary">
                  <BulletCheck />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {needsUpgrade ? (
              <button className="pricing-summary__cta" onClick={() => handleUpgrade('pricing_summary')}>
                {t('premium.cta')}
              </button>
            ) : (
              <p className="pricing-summary__note">{t('summary.premium.note')}</p>
            )}
          </article>
        </section>

        <p className="pricing-summary__compare-link">
          <a href="#pricing-compare-table">{t('summary.compareAll')}</a>
        </p>

        <section id="pricing-compare-table" className="pricing-compare" aria-label={t('compare.premium') + ' vs ' + t('compare.free')}>
          <div className="pricing-compare__header-cell is-spacer" aria-hidden="true" />
          <div className="pricing-compare__header-cell is-free">
            <span className="pricing-compare__plan-name">{t('compare.free')}</span>
            <span className="pricing-compare__plan-price">
              <span className="pricing-compare__sym">{pricing.symbol}</span>
              <span className="pricing-compare__amt">0</span>
              <span className="pricing-compare__per">/{t('perMonthShort')}</span>
            </span>
          </div>
          <div className="pricing-compare__header-cell is-premium">
            <span className="pricing-compare__plan-name">{t('compare.premium')}</span>
            <span className="pricing-compare__plan-price">
              {isYearly && pricing.approximate && <span className="pricing-compare__approx">≈</span>}
              <span className="pricing-compare__sym">{pricing.symbol}</span>
              <span className="pricing-compare__amt">{priceAmount}</span>
              <span className="pricing-compare__per">/{t('perMonthShort')}</span>
            </span>
            {isYearly && (
              <span className="pricing-compare__billed">
                {t('billedYearly', { symbol: pricing.symbol, amount: pricing.yearly })}
              </span>
            )}
          </div>

          {FEATURE_SECTIONS.map((section) => (
            <Fragment key={section.titleKey}>
              <div className="pricing-compare__section-title">{t(section.titleKey)}</div>
              {section.features.map((f) => (
                <Fragment key={f.key}>
                  <div className="pricing-compare__feature">
                    <span className="pricing-compare__feature-label">{t(f.key)}</span>
                    {f.subKey && (
                      <span className="pricing-compare__feature-sub">{t(f.subKey)}</span>
                    )}
                  </div>
                  <div className={`pricing-compare__cell is-free-cell is-state-${f.free}`}>
                    {renderFreeCell(f.free)}
                  </div>
                  <div className="pricing-compare__cell is-premium-cell">
                    {f.premiumKey ? (
                      <span className="pricing-compare__note">{t(f.premiumKey)}</span>
                    ) : (
                      <CheckPremium />
                    )}
                  </div>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </section>

        <div className="pricing-cta-block">
          <button
            className="pricing-offer__cta"
            onClick={() => handleUpgrade('pricing_cta_block')}
          >
            {isSubscribed
              ? (isTrialing ? t('premium.cta') : t('manageSubscription'))
              : t('premium.cta')}
          </button>
          <p className="pricing-offer__guarantee">{t('guarantee')}</p>
        </div>

        {isSubscribed && currentPeriod && !isTrialing && (
          <section className="pricing-switch" aria-label="Switch billing period">
            <div className="pricing-switch__info">
              <span className="pricing-switch__icon"><SwapIcon /></span>
              <div>
                <strong className="pricing-switch__title">
                  {isUpgrade ? t('switchToYearly') : t('switchToMonthly')}
                </strong>
                <span className="pricing-switch__detail">
                  {isUpgrade
                    ? t('switchToYearlySub', { symbol: pricing.symbol, amount: pricing.yearly, saved: pricing.approximate ? '≈36%' : '36%' })
                    : t('switchToMonthlySub', { symbol: pricing.symbol, amount: pricing.monthly })}
                </span>
              </div>
            </div>
            <button
              className={`pricing-switch__btn ${isUpgrade ? 'is-upgrade' : 'is-downgrade'}`}
              onClick={handleSwitchPlan}
            >
              {isUpgrade ? t('upgradeNow') : t('switchPlan')}
            </button>
          </section>
        )}

        <p className="pricing-faq-link">
          <Link to="/faq">{t('seeFaq')}</Link>
        </p>

        <footer className="pricing-credit">{t('credit')}</footer>
      </div>

      <PurchaseComingSoonModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        source={waitlistSource}
      />
    </article>
  );
};

export default PricingPage;
